import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import { useAuth } from '../auth/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { FEATURE_PERMISSIONS } from '../../utils/permissions';
import { Link } from 'react-router-dom';
import Tooltip from '../common/Tooltip';

/**
 * Re-Engagement Queue — shows flagged Closed Lost / Not Qualified opportunities
 * for managers/admins. Self-manages its own Firestore subscription.
 *
 * @param {Map} usersById - Map of user objects keyed by uid (from parent page)
 * @param {string} [className] - Optional wrapper className for spacing
 */
export default function ReEngagementQueue({ usersById, className = '' }) {
    const { currentUser: user } = useAuth();
    const { can } = usePermissions(user);
    const [followUpOpps, setFollowUpOpps] = useState([]);

    useEffect(() => {
        if (!user?.uid || !can(FEATURE_PERMISSIONS.OPP_VIEW_ALL)) return;
        let unsub = () => {};
        try {
            unsub = onSnapshot(
                query(
                    collection(db, collectionPath('opportunities')),
                    where('followUpFlag', '==', true),
                    orderBy('followUpDate', 'asc')
                ),
                (snapshot) => {
                    const raw = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    setFollowUpOpps(raw.filter(o => !o.followUpCompleted));
                },
                (err) => { console.error('Follow-up queue fetch error:', err); }
            );
        } catch (err) {
            console.error('Follow-up queue subscription error:', err);
        }
        return () => { try { unsub(); } catch (e) {} };
    }, [user, can]);

    if (!can(FEATURE_PERMISSIONS.OPP_VIEW_ALL) || followUpOpps.length === 0) return null;

    return (
        <div className={className}>
            <div className="flex items-center gap-2 mb-4">
                <h2 className="text-2xl font-bold">Re-Engagement Queue</h2>
                <span className="text-sm px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">
                    {followUpOpps.length} pending
                </span>
                <Tooltip text="Closed Lost / Not Qualified opportunities flagged for future re-engagement, ordered by follow-up date" maxWidth={320}>
                    <svg className="w-5 h-5 text-gray-500 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </Tooltip>
            </div>
            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <table className="w-full whitespace-no-wrap">
                    <thead>
                        <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50">
                            <th className="px-4 py-3">Opportunity</th>
                            <th className="px-4 py-3">Customer</th>
                            <th className="px-4 py-3">Follow-Up Date</th>
                            <th className="px-4 py-3">Notes</th>
                            <th className="px-4 py-3">Sales Rep</th>
                            <th className="px-4 py-3">Flagged By</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y">
                        {followUpOpps.map(opp => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            let isOverdue = false;
                            let isDueToday = false;
                            if (opp.followUpDate) {
                                const d = new Date(opp.followUpDate);
                                d.setHours(0, 0, 0, 0);
                                isOverdue = d < today;
                                isDueToday = d.getTime() === today.getTime();
                            }
                            const assignedUser = usersById?.get(opp.uid);
                            const repName = assignedUser
                                ? (
                                    assignedUser.firstName || assignedUser.lastName
                                      ? `${assignedUser.firstName || ''} ${assignedUser.lastName || ''}`.trim()
                                      : assignedUser.displayName || assignedUser.email || assignedUser.uid
                                  )
                                : (opp.salesReps?.[0]?.salesRepName || opp.salesRepNames?.[0] || '\u2014');
                            return (
                                <tr key={opp.id} className={`hover:bg-gray-50 ${isOverdue ? 'bg-red-50' : ''}`}>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <Link to={`/opportunity/${opp.id}`} className="text-indigo-600 hover:underline font-medium">
                                            {opp.opportunityNumberLabel || opp.opportunityNumber || opp.id}
                                        </Link>
                                    </td>
                                    <td className="px-4 py-3 max-w-[180px] truncate" title={opp.customerName}>
                                        {opp.customerName || '\u2014'}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        {opp.followUpDate ? (
                                            <span className={`font-medium ${isOverdue ? 'text-red-600' : isDueToday ? 'text-amber-600' : 'text-gray-800'}`}>
                                                {new Date(opp.followUpDate).toLocaleDateString()}
                                                {isOverdue && <span className="ml-2 text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">Overdue</span>}
                                                {isDueToday && <span className="ml-2 text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">Today</span>}
                                            </span>
                                        ) : '\u2014'}
                                    </td>
                                    <td className="px-4 py-3 max-w-[240px] truncate text-gray-600 text-sm" title={opp.followUpNotes}>
                                        {opp.followUpNotes || '\u2014'}
                                    </td>
                                    <td className="px-4 py-3">
                                        {repName}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500">
                                        {opp.followUpSetByName || '\u2014'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
